import { useMemo } from 'react'
import { createSearchParams } from 'react-router-dom'
import {
  Box,
  Button,
  Flex,
  Loading,
  Text,
  Table,
  createColumnHelper,
  toast,
} from 'ui'
import { formatInTimeZone } from 'date-fns-tz'
import { useCompanyInfo } from 'queries/company'
import {
  useInvoicePreviewDownloadMutation,
  useUpcomingInvoices,
} from 'queries/payment'
import { QueryData } from 'queries/types'
import { formatCurrency } from 'lib/string'
import { ModalLink } from 'components/ModalLink'
import { InvoiceGroupCell } from 'components/InvoiceGroupCell'

type UpcomingInvoice = QueryData<typeof useUpcomingInvoices>[number]

const DEFAULT_WEEK_TIMEZONE = 'America/Los_Angeles'
const DATE_FORMAT = 'MM/dd/yyyy zzz'

function formatInvoiceDate(value: string, weekTimezone?: string) {
  return formatInTimeZone(
    new Date(value),
    weekTimezone || DEFAULT_WEEK_TIMEZONE,
    DATE_FORMAT
  )
}

const columnHelper = createColumnHelper<UpcomingInvoice>()

const upcomingInvoiceColumns = [
  columnHelper.accessor('periodStart', {
    header: () => <Text style={{ whiteSpace: 'nowrap' }}>Period</Text>,
    cell: (info) => {
      const { periodEnd, weekTimezone } = info.row.original
      const periodStart = formatInvoiceDate(info.getValue(), weekTimezone)
      const formattedPeriodEnd = formatInvoiceDate(periodEnd, weekTimezone)
      return (
        <Text style={{ whiteSpace: 'nowrap' }}>
          {periodStart} – {formattedPeriodEnd}
        </Text>
      )
    },
  }),
  columnHelper.accessor('invoiceGroup', {
    header: () => <Text style={{ whiteSpace: 'nowrap' }}>Invoice group</Text>,
    cell: (info) => (
      <InvoiceGroupCell
        invoiceGroup={info.getValue()}
        invoiceGroupKey={info.row.original.invoiceGroupKey}
      />
    ),
  }),
  columnHelper.accessor('estimateTotalCharge', {
    header: () => (
      <Text textAlign="right" pr={[0, 100]} style={{ whiteSpace: 'nowrap' }}>
        Est. amount
      </Text>
    ),
    cell: (info) => (
      <Text textAlign="right" pr={[0, 100]}>
        {formatCurrency(info.getValue())}
      </Text>
    ),
  }),
  columnHelper.accessor('cutoffAt', {
    header: () => <Text style={{ whiteSpace: 'nowrap' }}>Invoice date</Text>,
    cell: (info) => (
      <Text style={{ whiteSpace: 'nowrap' }}>
        {formatInvoiceDate(info.getValue(), info.row.original.weekTimezone)}
      </Text>
    ),
  }),
  columnHelper.display({
    id: 'details',
    header: '',
    size: 120,
    cell: (info) => {
      const row = info.row.original
      return (
        <ModalLink
          to={{
            pathname: '/payments/upcoming-invoice',
            search: createSearchParams({
              periodStart: row.periodStart,
              invoiceGroupKey: row.invoiceGroupKey,
            }).toString(),
          }}
        >
          <Button width={120} kind="small">
            Details
          </Button>
        </ModalLink>
      )
    },
  }),
]

export function UpcomingInvoices() {
  const { data: company } = useCompanyInfo()
  const { data: upcomingInvoices, isLoading } = useUpcomingInvoices(company?.id)
  const { mutateAsync: downloadInvoicePreview, isPending: isDownloading } =
    useInvoicePreviewDownloadMutation(company?.id)

  const total = useMemo(() => {
    if (!upcomingInvoices) {
      return 0
    }

    return upcomingInvoices.reduce((acc, curr) => {
      return acc + Number(curr.estimateTotalCharge)
    }, 0)
  }, [upcomingInvoices])

  const handleDownload = async () => {
    try {
      await downloadInvoicePreview()
    } catch {
      toast.error('Unable to generate CSV, please try again later.')
    }
  }

  if (isLoading) {
    return <Loading />
  }

  return (
    <Box py={3}>
      <Flex justifyContent="space-between" alignItems="center" mb={3}>
        <Text>
          Total estimated: <strong>{formatCurrency(total)}</strong>
        </Text>
        <Button
          variant="secondary"
          loading={isDownloading}
          disabled={!company}
          onClick={handleDownload}
        >
          Download CSV
        </Button>
      </Flex>
      <Table columns={upcomingInvoiceColumns} data={upcomingInvoices ?? []} />
      {(upcomingInvoices ?? []).length === 0 ? (
        <Text p={3} color="neutrals.200">
          No Upcoming Invoice
        </Text>
      ) : null}
    </Box>
  )
}
